import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { 
  Search, RefreshCw, Users, Activity, BarChart3, ExternalLink, 
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import tokenService from '../services/tokenService';
import { useTokenConfig } from '../contexts/TokenConfigContext';
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const TokenTrackerExtended = () => {
  const { tokenAddress: globalTokenAddress, tokenSymbol } = useTokenConfig();
  const [tokenAddress, setTokenAddress] = useState(globalTokenAddress);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [price, setPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [trades, setTrades] = useState([]);
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [priceDays, setPriceDays] = useState(7);

  const fetchTokenData = async (address = tokenAddress) => {
    if (!address) {
      toast.error('Please enter a token address');
      return;
    }

    setLoading(true);
    try {
      // Fetch all data in parallel
      const [info, priceData, historyData, tradesData, holdersData] = await Promise.all([
        tokenService.getTokenInfo(address),
        tokenService.getPrice(address),
        tokenService.getPriceHistory(address, priceDays),
        tokenService.getTrades(address, 50),
        tokenService.getHolders(address, 10000),  // Show all holders
      ]);

      setTokenInfo(info);
      setPrice(priceData);
      setPriceHistory(historyData.history || []);
      setTrades(tradesData.trades || []);
      setHolders(holdersData);

      console.log('Loaded trades:', tradesData.trades);
      toast.success(`Loaded ${info.name} - ${tradesData.trades?.length || 0} trades found`);
    } catch (error) {
      toast.error('Failed to load token data', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenAddress) {
      fetchTokenData();
    }
  }, []);

  useEffect(() => {
    if (!autoRefresh || !tokenAddress) return;

    const interval = setInterval(() => {
      fetchTokenData();
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, tokenAddress]);

  const handleSearch = () => {
    fetchTokenData();
  };

  const formatBalance = (balance, decimals = 9) => {
    const num = parseFloat(balance) / Math.pow(10, parseInt(decimals));
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 6)}`;
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    const num = parseFloat(price);
    if (num < 0.000001) {
      return `$${num.toExponential(4)}`;
    }
    return `$${num.toFixed(8)}`;
  };

  const getPriceChange = (diff) => {
    if (!diff || !diff.USD) return { value: '0', isPositive: true };
    const value = diff.USD.replace('−', '-').replace('%', '');
    const num = parseFloat(value);
    return { value: value + '%', isPositive: num >= 0 };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getTradeIcon = (type) => {
    if (type === 'swap') return <TrendingUp className="h-5 w-5 text-purple-600" />;
    return <Activity className="h-5 w-5 text-blue-600" />;
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Token Tracker
          </CardTitle>
          <CardDescription>
            Track any Jetton token in real-time on TON blockchain
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Enter token address (EQ...)"
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              className="font-mono text-sm"
            />
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="h-4 w-4 mr-2" />
              Track
            </Button>
            <Button variant="outline" onClick={() => fetchTokenData()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <input
              type="checkbox"
              id="auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="auto-refresh" className="text-sm text-gray-600">
              Auto-refresh every 15 seconds
            </label>
          </div>
        </CardContent>
      </Card>

      {tokenInfo && (
        <>
          {/* Price & Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Current Price</p>
                  <p className="text-2xl font-bold">{formatPrice(price?.price_usd)}</p>
                  {price && (
                    <div className="flex items-center gap-2 text-sm">
                      {getPriceChange(price.diff_24h).isPositive ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <span className={getPriceChange(price.diff_24h).isPositive ? 'text-green-600' : 'text-red-600'}>
                        {getPriceChange(price.diff_24h).value}
                      </span>
                      <span className="text-gray-500">24h</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Token</p>
                  <p className="text-xl font-bold">{tokenInfo.name}</p>
                  <p className="text-sm text-gray-500">{tokenInfo.symbol}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Total Supply</p>
                  <p className="text-xl font-bold">
                    {formatBalance(tokenInfo.total_supply, tokenInfo.decimals)}
                  </p>
                  <p className="text-sm text-gray-500">{tokenInfo.symbol}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Holders</p>
                  <p className="text-xl font-bold">{holders.length}</p>
                  <p className="text-sm text-gray-500">Addresses</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Price Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Price Chart</CardTitle>
                  <CardDescription>Historical price data</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={priceDays === 1 ? 'default' : 'outline'}
                    onClick={() => setPriceDays(1)}
                  >
                    1D
                  </Button>
                  <Button
                    size="sm"
                    variant={priceDays === 7 ? 'default' : 'outline'}
                    onClick={() => setPriceDays(7)}
                  >
                    7D
                  </Button>
                  <Button
                    size="sm"
                    variant={priceDays === 30 ? 'default' : 'outline'}
                    onClick={() => setPriceDays(30)}
                  >
                    30D
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {priceHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={priceHistory}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `$${value.toFixed(8)}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#3b82f6" 
                      fillOpacity={1} 
                      fill="url(#colorPrice)"
                      name="Price (USD)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No price data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trades & Holders Tabs */}
          <Tabs defaultValue="trades" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="trades">Recent Trades</TabsTrigger>
              <TabsTrigger value="holders">Top Holders</TabsTrigger>
            </TabsList>

            <TabsContent value="trades" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Trades & Transfers</CardTitle>
                  <CardDescription>{trades.length} recent activities</CardDescription>
                </CardHeader>
                <CardContent>
                  {trades.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No recent trades
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {trades.map((trade, index) => (
                          <div
                            key={index}
                            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-1">{getTradeIcon(trade.type)}</div>
                                <div className="flex-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant={trade.type === 'swap' ? 'default' : 'secondary'}>
                                      {trade.type}
                                    </Badge>
                                    <span className="text-xs text-gray-500">
                                      {new Date(trade.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                  
                                  {trade.type === 'swap' && (
                                    <div className="text-sm">
                                      <p className="font-medium">
                                        {formatBalance(trade.amount_in)} → {formatBalance(trade.amount_out)}
                                      </p>
                                      <p className="text-gray-500">DEX: {trade.dex || 'Unknown'}</p>
                                    </div>
                                  )}
                                  
                                  {trade.type === 'transfer' && (
                                    <div className="text-sm">
                                      <p className="font-medium">
                                        Amount: {trade.jetton ? 
                                          formatBalance(trade.amount, trade.jetton.decimals) : 
                                          formatBalance(trade.amount)} {trade.jetton?.symbol || tokenInfo.symbol}
                                      </p>
                                      <p className="text-gray-500">
                                        From: {formatAddress(trade.sender)}
                                      </p>
                                      <p className="text-gray-500">
                                        To: {formatAddress(trade.recipient)}
                                      </p>
                                      {trade.comment && (
                                        <p className="text-xs text-gray-400 mt-1">
                                          💬 {trade.comment}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="holders" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Holders</CardTitle>
                  <CardDescription>Addresses holding {tokenInfo.symbol} tokens</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {holders.slice(0, 20).map((holder, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-gray-500">#{index + 1}</div>
                            <div>
                              <p className="text-sm font-mono">{formatAddress(holder.address)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">
                              {formatBalance(holder.balance, tokenInfo.decimals)}
                            </p>
                            <p className="text-xs text-gray-500">{tokenInfo.symbol}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {!tokenInfo && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Enter a token address to start tracking
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TokenTrackerExtended;
